<template>
  <div class="integration-connect-card">
    <m-card class="_card">
      <div class="_topbar">
        <img
          class="_logo"
          src="@/assets/img/logo.svg"
          alt="Mooncamp"
        >
        <div
          v-if="accounts.length > 0"
          class="_account"
        >
          <m-dropdown
            close-on-click
          >
            <m-btn
              hide-border
              small
              class="_account-btn"
            >
              <m-avatar
                :src="selectedAccount?.companyImage?.getURL || ''"
                :text="accountName(selectedAccount)"
                :amount-chars="1"
                :size="20"
              />
              <span class="_account-name">{{ accountName(selectedAccount) }}</span>
              <m-icon
                type="down"
                size="12"
              />
            </m-btn>
            <template #overlay>
              <m-card
                no-padding
                list
                class="_account-menu"
              >
                <m-card-item
                  v-for="account in accounts"
                  :key="account.uid"
                  @click="selectAccount(account)"
                >
                  <template #left>
                    <m-avatar
                      :src="account.companyImage?.getURL || ''"
                      :text="accountName(account)"
                      :amount-chars="1"
                      :size="22"
                    />
                  </template>
                  <div class="_name">
                    {{ accountName(account) }}
                  </div>
                  <template
                    v-if="account.uid === selectedAccountId"
                    #right
                  >
                    <m-icon
                      type="check"
                      size="16"
                    />
                  </template>
                </m-card-item>
              </m-card>
            </template>
          </m-dropdown>
        </div>
      </div>

      <div class="_hero">
        <m-icon
          :type="logoIcon"
          size="52"
          class="_hero-logo"
        />
        <h2 class="_title">
          {{ title }}
        </h2>
        <p class="_body">
          {{ body }}
        </p>
      </div>

      <div class="_permissions">
        <p class="_perm-heading">
          {{ permissionsHeading }}
        </p>
        <div
          v-for="permission in permissions"
          :key="permission.icon"
          class="_perm-row"
        >
          <m-icon
            :type="permission.icon"
            size="18"
            class="_perm-icon"
          />
          <span>{{ permission.text }}</span>
        </div>
      </div>

      <div class="_actions">
        <m-btn
          block
          class="_action-btn"
          @click="$emit('cancel')"
        >
          {{ cancelLabel }}
        </m-btn>
        <m-btn
          block
          color="primary"
          class="_action-btn"
          :loading="loading"
          @click="$emit('allow', selectedAccount)"
        >
          {{ allowLabel }}
        </m-btn>
      </div>
    </m-card>
  </div>
</template>

<script setup>
import useMe from '@/composables/me';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

defineProps({
  logoIcon: { type: String, required: true },
  title: { type: String, default: '' },
  body: { type: String, default: '' },
  permissionsHeading: { type: String, default: '' },
  permissions: { type: Array, default: () => [] },
  allowLabel: { type: String, default: '' },
  cancelLabel: { type: String, default: '' },
  loading: { type: Boolean, default: false },
});

defineEmits(['allow', 'cancel']);

const { reachableAccounts, lastActiveAccount } = useMe();

const accounts = computed(() => reachableAccounts.value ?? []);
const accountName = (account) => account?.companyName || account?.displayName || t('accountPicker.untitledAccount');

const selectedAccountId = ref(lastActiveAccount.value?.uid ?? accounts.value[0]?.uid ?? null);
const selectedAccount = computed(
  () => accounts.value.find((account) => account.uid === selectedAccountId.value) ?? null,
);

const selectAccount = (account) => {
  selectedAccountId.value = account.uid;
};
</script>

<style scoped lang="scss" type="text/scss">
  .integration-connect-card {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
    padding: 2.4rem;
    background-color: $side-nav-background-color;

    ._card {
      width: 100%;
      max-width: 52rem;
    }

    ._topbar {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding-bottom: 1.6rem;
      border-bottom: 1px solid $border-color;

      ._logo {
        height: 2.6rem;
      }

      ._account {
        display: flex;
        align-items: center;
      }

      ._account-btn {
        display: flex;
        align-items: center;
        gap: .6rem;
      }

      ._account-name {
        max-width: 18rem;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-size: $font-size-5;
      }
    }

    ._hero {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 3.6rem 2rem 2.4rem;
      text-align: center;

      ._hero-logo {
        margin-bottom: 1.6rem;
      }

      ._title {
        margin: 0 0 1.2rem;
        font-size: $font-size-8;
        font-weight: $font-weight-bold;
        color: $font-color-primary;
      }

      ._body {
        max-width: 42rem;
        margin: 0;
        color: $font-color-secondary;
        font-size: $font-size-5;
        line-height: 1.55;
      }
    }

    ._permissions {
      padding-bottom: 2.4rem;

      ._perm-heading {
        margin: 0 0 1.2rem;
        font-size: $font-size-5;
        color: $font-color-primary;
      }

      ._perm-row {
        display: flex;
        align-items: center;
        gap: 1.2rem;
        padding: 1.2rem 1.6rem;
        border: 1px solid $border-color;
        border-radius: $border-radius-sm;
        font-size: $font-size-5;
        color: $font-color-primary;

        & + ._perm-row {
          margin-top: .8rem;
        }

        ._perm-icon {
          flex-shrink: 0;
          color: $font-color-secondary;
        }
      }
    }

    ._actions {
      display: flex;
      gap: 1rem;
      padding-top: 1.6rem;
      border-top: 1px solid $border-color;

      ._action-btn {
        flex: 1;
      }
    }

  }

  ._account-menu {
    min-width: 20rem;

    ._name {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-size: $font-size-5;
      max-width: 17rem;
    }
  }
</style>
